import { Grid, RadioGroup, Radio, FormControlLabel, Tooltip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useState, useCallback, useEffect } from "react";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";
import { getPreviousWeekday, getNextWeekday } from "../../../helpers";
import { DateSelectionMode } from "../../../constants";

/**
 * The "Bucket Inception" option (Strategy Report only). `range` is null while
 * it is loading or when none exists; `disabledReason` is the tooltip shown on
 * the disabled option when none exists (null while loading).
 */
export interface BucketInceptionOption {
  range: [string, string] | null;
  disabledReason: string | null;
}

interface DateSelectorProps {
  onDateChange?: (range: [string | null, string | null]) => void;
  defaultRange?: [string | null, string | null];
  style?: React.CSSProperties;
  additionalComponents?: React.ReactNode | null;
  inceptionDate?: string | null;
  removedDate?: string | null;
  bucketInception?: BucketInceptionOption;
}

const DEFAULT_RANGE: [string | null, string | null] = [null, null];

const formatRange = ([startDate, endDate]: [string, string]) =>
  `${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format("MM/DD/YYYY")}`;

const DateSelector = ({ defaultRange, onDateChange, style, additionalComponents, inceptionDate, removedDate, bucketInception }: DateSelectorProps) => {
  const [selectionMode, setSelectionMode] = useState<DateSelectionMode>(DateSelectionMode.YEAR);
  const [range, setRange] = useState<[string | null, string | null]>(DEFAULT_RANGE);

  // The option's range can vanish while it is picked (another account or
  // Bucket 3): leave nothing checked-but-disabled with no picker beside it.
  const bucketInceptionUnavailable = !!bucketInception && !bucketInception.range;
  useEffect(() => {
    if (bucketInceptionUnavailable) {
      setSelectionMode((mode) =>
        mode === DateSelectionMode.BUCKET_INCEPTION ? DateSelectionMode.YEAR : mode,
      );
    }
  }, [bucketInceptionUnavailable]);

  const handleModeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const mode = event.target.value as DateSelectionMode;
    setSelectionMode(mode);
    if (mode === DateSelectionMode.INCEPTION && inceptionDate) {
      const endDate = removedDate
        ? removedDate
        : getPreviousWeekday(dayjs().format("YYYY-MM-DD")).format("YYYY-MM-DD");
      onDateChange?.([inceptionDate, endDate]);
    }
    if (mode === DateSelectionMode.BUCKET_INCEPTION && bucketInception?.range) {
      onDateChange?.(bucketInception.range);
    }
  }, [inceptionDate, removedDate, onDateChange, bucketInception?.range]);

  const handleYearChange = useCallback((value: Dayjs | null) => {
    if (!value) {
      setRange(DEFAULT_RANGE);
      onDateChange?.(DEFAULT_RANGE);
      return;
    }

    const startDate = value.format("YYYY") + "-01-01";
    let endDate = value.format("YYYY") + "-12-31";
    if (value.format("YYYY") === dayjs().year().toString()) {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    const adjStartDate = getNextWeekday(startDate);
    const adjEndDate = getPreviousWeekday(endDate);
    const newRange: [string, string] = [adjStartDate.format("YYYY-MM-DD"), adjEndDate.format("YYYY-MM-DD")];
    setRange(newRange);
    onDateChange?.(newRange);
    return;
  }, [onDateChange]);

  const handleMonthChange = useCallback((value: Dayjs | null) => {
    if (!value) {
      setRange(DEFAULT_RANGE);
      onDateChange?.(DEFAULT_RANGE);
      return;
    }

    const startDate = value.format("YYYY-MM") + "-01";
    let endDate = value.endOf('month').format("YYYY-MM-DD");
    if (value.format("YYYY-MM") === dayjs().format("YYYY-MM")) {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    const adjStartDate = getNextWeekday(startDate);
    const adjEndDate = getPreviousWeekday(endDate);
    const newRange: [string, string] = [adjStartDate.format("YYYY-MM-DD"), adjEndDate.format("YYYY-MM-DD")];
    setRange(newRange);
    onDateChange?.(newRange);
    return;
  }, [onDateChange]);

  const handleRangeChange = useCallback((dates: DateObject[]) => {
    if (dates.length === 2) {
      const [startDate, endDate] = dates;
      const adjStartDate = getNextWeekday(startDate.format("YYYY-MM-DD"));

      let adjEndDate = getPreviousWeekday(endDate.format("YYYY-MM-DD"));
      if (adjEndDate.format("YYYY-MM-DD") > dayjs().format("YYYY-MM-DD")) {
        adjEndDate = getPreviousWeekday(dayjs().format("YYYY-MM-DD"));
      }

      const newRange: [string, string] = [
        adjStartDate.format("YYYY-MM-DD"),
        adjEndDate.format("YYYY-MM-DD"),
      ];
      setRange(newRange);
      onDateChange?.(newRange);
      return;
    }

    setRange(DEFAULT_RANGE);
    onDateChange?.(DEFAULT_RANGE);
  }, [onDateChange]);

  const formatPreviousRange = useCallback(() => {
    if (!defaultRange?.[0] || !defaultRange?.[1]) return "Pick range";
    return `${dayjs(defaultRange[0]).format("MM/DD/YYYY")} - ${dayjs(defaultRange[1]).format("MM/DD/YYYY")}`;
  }, [defaultRange]);

  return (
    <Grid container sx={{ paddingY: 1, paddingLeft: 1, justifyContent: style ? 'flex-start' : 'flex-end' }} alignItems={style ? 'flex-start' : 'center'}>
      <Grid item marginRight={2}>
        <RadioGroup
          row
          value={selectionMode}
          onChange={handleModeChange}
          aria-label="date selection mode"
          sx={style ? { ...style } : {}}
        >
          <FormControlLabel value={DateSelectionMode.YEAR} control={<Radio />} label="Year" />
          <FormControlLabel value={DateSelectionMode.MONTH} control={<Radio />} label="Month" />
          <FormControlLabel value={DateSelectionMode.RANGE} control={<Radio />} label="Range" />
          {bucketInception && (
            <Tooltip
              title={!bucketInception.range ? bucketInception.disabledReason ?? "" : ""}
              placement="left"
            >
              {/* A disabled control fires no mouse events; the span carries the hover. */}
              <span style={{ display: "inline-flex" }}>
                <FormControlLabel
                  value={DateSelectionMode.BUCKET_INCEPTION}
                  control={<Radio />}
                  disabled={!bucketInception.range}
                  label={bucketInception.range
                    ? `Bucket Inception (${formatRange(bucketInception.range)})`
                    : "Bucket Inception"}
                />
              </span>
            </Tooltip>
          )}
          {inceptionDate && (
            <FormControlLabel value={DateSelectionMode.INCEPTION} control={<Radio />} label="Since Inception" />
          )}
        </RadioGroup>
      </Grid>
      <Grid item marginRight={2}>
        {selectionMode === DateSelectionMode.BUCKET_INCEPTION ? null : selectionMode === DateSelectionMode.RANGE ? (
          <CustomDatePicker
            range
            format="MM/DD/YYYY"
            onChange={handleRangeChange}
            dateSeparator=" - "
            style={{ height: "56px", width: "200px", fontSize: "16px" }}
            placeholder={formatPreviousRange()}
            showOtherDays
            calendarPosition="top"
            portal
            zIndex={1700}
            aria-label="Select date range"
          />
        ) : (
          <DatePicker
            key={selectionMode}
            views={selectionMode === DateSelectionMode.YEAR ? [DateSelectionMode.YEAR] : [DateSelectionMode.YEAR, DateSelectionMode.MONTH]}
            format={selectionMode === DateSelectionMode.YEAR ? "YYYY" : "MM/YYYY"}
            onAccept={selectionMode === DateSelectionMode.YEAR ? handleYearChange : handleMonthChange}
            minDate={dayjs('2023-01-01')}
            sx={{ height: "56px", width: "200px" }}
            slotProps={{
              textField: {
                placeholder: defaultRange ? defaultRange[0]?.split("-")[0] : `Pick ${selectionMode}`,
                "aria-label": `Select ${selectionMode}`
              },
              popper: {
                sx: { zIndex: 1700 },
              },
            }}
          />
        )}
      </Grid>
      <Grid item>{additionalComponents}</Grid>
    </Grid>
  );
};

export default DateSelector;
